import { useEffect, useState } from 'react';

/**
 * Instalar o app na tela inicial do celular.
 *
 * ── O que dá e o que NÃO dá ───────────────────────────────────────────────
 *
 * O pedido foi "mostrar um modal perguntando se ela gostaria de fazer download
 * do app no celular, e aí faz o download e abre automaticamente". Duas dessas
 * três coisas existem:
 *
 *  ✓ O modal, decidido por nós, na hora que quisermos.
 *  ✓ O botão que abre a instalação de verdade — no Android/Chrome.
 *  ✗ **Abrir sozinho depois de instalar. Isso não existe em nenhum
 *    navegador.** Não há API na web que lance um app instalado; seria uma
 *    página poder abrir programas no aparelho de quem a visita, e é justamente
 *    o que os navegadores impedem. Depois de instalar, quem abre é a pessoa,
 *    tocando no ícone.
 *
 * E no iPhone nem a instalação é programável: o Safari não implementa
 * `beforeinstallprompt`. Lá o caminho é Compartilhar → "Adicionar à Tela de
 * Início", e o máximo honesto é ENSINAR. Um botão "Instalar" que não instala
 * seria pior que não ter botão.
 *
 * ── Por que a captura é no escopo do MÓDULO ──────────────────────────────
 *
 * `beforeinstallprompt` dispara uma vez e não repete. Se o ouvinte nascesse
 * dentro de um componente, todo evento disparado antes daquele componente
 * montar estaria perdido — e o botão nunca funcionaria, sem erro nenhum.
 *
 * Aqui o ouvinte existe desde que o arquivo é importado, e guarda o evento
 * para quem chegar depois.
 *
 * ── Quando o Chrome dispara ──────────────────────────────────────────────
 *
 * Não é imediato. Pelos critérios dele, a pessoa precisa ter tocado na página
 * pelo menos uma vez e passado 30 segundos nela — em qualquer visita, não
 * necessariamente nesta. Por isso o modal não pode ser mostrado "assim que
 * loga": ele espera o evento, e o evento pode chegar minutos depois ou já ter
 * chegado numa sessão anterior.
 */

/** O evento do Chrome, que o TypeScript ainda não tipa. */
interface EventoDeInstalacao extends Event {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>;
}

let guardado: EventoDeInstalacao | null = null;
const ouvintes = new Set<() => void>();

const avisar = () => ouvintes.forEach(f => f());

if (typeof window !== 'undefined') {
  window.addEventListener('beforeinstallprompt', e => {
    // Sem isto o Chrome mostra a própria barrinha de instalação, e teríamos
    // dois convites competindo — o nosso e o dele.
    e.preventDefault();
    guardado = e as EventoDeInstalacao;
    avisar();
  });

  // Instalou (pelo nosso botão ou pelo menu do navegador): o convite morre.
  window.addEventListener('appinstalled', () => {
    guardado = null;
    avisar();
  });
}

/**
 * Onde o app vai parar: no celular ou no computador.
 *
 * ── Por que isto existe ──────────────────────────────────────────────────
 *
 * O app instala nos DOIS. No Chrome e no Edge do computador ele ganha ícone
 * próprio e abre em janela separada, sem barra do navegador — exatamente como
 * no celular.
 *
 * Eu tinha escrito "Adicionar app no celular" e "Receber avisos no celular"
 * como texto fixo, e o item do menu não tem trava de largura: no desktop ele
 * aparecia dizendo "celular". Rótulo que descreve o aparelho errado é pior que
 * rótulo genérico — ele faz a pessoa achar que a opção não é para ela.
 *
 * ── `pointer: coarse` e não a largura da tela ────────────────────────────
 *
 * Largura mede a JANELA, e uma janela estreita no computador não vira celular.
 * `pointer: coarse` mede o apontador primário: dedo contra mouse. É a pergunta
 * certa — "isto é um aparelho de toque?" — e não muda quando alguém redimensiona
 * o navegador.
 */
export const ondeInstala = (): 'celular' | 'computador' => {
  if (typeof window === 'undefined') return 'celular';
  return window.matchMedia('(pointer: coarse)').matches ? 'celular' : 'computador';
};

/** O app já está rodando instalado? */
export const jaInstalado = (): boolean => {
  if (typeof window === 'undefined') return false;
  // O primeiro cobre Android e desktop; `navigator.standalone` é o do iOS, que
  // não implementa `display-mode`.
  return window.matchMedia('(display-mode: standalone)').matches
    || (window.navigator as { standalone?: boolean }).standalone === true;
};

/**
 * É um iPhone ou iPad?
 *
 * O iPadOS se identifica como Macintosh desde o 13, então o teste do nome não
 * basta: um Mac com tela sensível ao toque não existe, e é isso que
 * `maxTouchPoints` separa.
 */
export const ehIOS = (): boolean => {
  if (typeof navigator === 'undefined') return false;
  const ua = navigator.userAgent;
  if (/iphone|ipod|ipad/i.test(ua)) return true;
  return /macintosh/i.test(ua) && navigator.maxTouchPoints > 1;
};

/** No iOS só o Safari instala; nos outros navegadores o menu não tem a opção. */
export const ehSafariNoIOS = (): boolean =>
  ehIOS() && !/crios|fxios|edgios|opios/i.test(navigator.userAgent);

export interface EstadoDaInstalacao {
  /** Dá para instalar com um toque (o evento do Chrome está na mão). */
  podeInstalar: boolean;
  /** É iPhone/iPad: dá para ensinar, não para instalar. */
  ensinaNoIOS: boolean;
  instalado: boolean;
}

export const useInstalacao = (): EstadoDaInstalacao => {
  const [temEvento, setTemEvento] = useState(guardado !== null);
  const [instalado, setInstalado] = useState(jaInstalado);

  useEffect(() => {
    const aoMudar = () => {
      setTemEvento(guardado !== null);
      setInstalado(jaInstalado());
    };
    ouvintes.add(aoMudar);
    aoMudar();
    return () => { ouvintes.delete(aoMudar); };
  }, []);

  return {
    podeInstalar: temEvento && !instalado,
    // Sem o evento e sendo iOS, o caminho é o manual. Fora do Safari nem isso:
    // o Chrome do iPhone não tem "Adicionar à Tela de Início".
    ensinaNoIOS: !temEvento && !instalado && ehSafariNoIOS(),
    instalado,
  };
};

/**
 * Abre a instalação do navegador. Devolve o que a pessoa escolheu.
 *
 * O evento só serve UMA vez — depois de usado, o Chrome não o entrega de novo
 * nesta navegação. Por isso ele é descartado aqui, independentemente da
 * resposta: guardar um evento gasto faria o botão aparecer e não fazer nada.
 */
export const instalar = async (): Promise<'accepted' | 'dismissed' | 'indisponivel'> => {
  const evento = guardado;
  if (!evento) return 'indisponivel';
  guardado = null;
  avisar();
  await evento.prompt();
  const { outcome } = await evento.userChoice;
  return outcome;
};

/**
 * O convite já foi recusado?
 *
 * Guardado no navegador porque é uma preferência daquele aparelho: instalar no
 * celular não diz nada sobre o computador, e recusar num não é recusar no
 * outro.
 */
export const CHAVE_DE_RECUSA = 'wallet-instalar-recusado';

/**
 * Quantos dias esperar antes de oferecer de novo.
 *
 * Perguntar uma vez e nunca mais perde quem recusou sem ler. Perguntar toda
 * vez é o pop-up que se aprende a fechar sem olhar. Trinta dias é longo o
 * bastante para não virar hábito de fechar.
 */
export const DIAS_ATE_OFERECER_DE_NOVO = 30;

export const recusouRecentemente = (agora = Date.now()): boolean => {
  try {
    const quando = Number(localStorage.getItem(CHAVE_DE_RECUSA));
    if (!quando || !Number.isFinite(quando)) return false;
    return (agora - quando) < DIAS_ATE_OFERECER_DE_NOVO * 86_400_000;
  } catch {
    // localStorage bloqueado: melhor oferecer do que travar o convite para
    // sempre por causa de uma preferência que não dá para ler.
    return false;
  }
};

export const marcarRecusa = (agora = Date.now()): void => {
  try {
    localStorage.setItem(CHAVE_DE_RECUSA, String(agora));
  } catch { /* sem localStorage, a recusa vale só para esta sessão */ }
};
